import React, {useEffect, useMemo, useState} from 'react';
import {Row, Col, Grid} from '@zendeskgarden/react-grid';
import { Field, Input, Label, MediaInput } from '@zendeskgarden/react-forms';
import { Datepicker } from '@zendeskgarden/react-datepickers';
import { Button } from '@zendeskgarden/react-buttons';
import {Controller, useForm} from 'react-hook-form';
import {LaptopDetails} from '../../models/Laptop.model';
import ClearButton from './clear-button';
import styled from 'styled-components';
import {useGetLaptopRepairAsync} from '../../hooks/useGetLaptopRepairAsync';
import {dateFormatter} from '../../helper/util';
import {useUpdateLaptopRepairAsync} from '../../hooks/useUpdateLaptopRepairAsync';
import ReplacementForm from './replacementForm';
import {Alert, Title, Close} from '@zendeskgarden/react-notifications';
import {getCategory} from '../../resources/zendesk';
import ConfirmSerialNumberModal from './ConfirmSerialNumberModal';
import RepairAlert from './RepairAlert';

type RepairInformationProps = {
    isShow: boolean
    information: LaptopDetails
    currentTicketId: string
}

export type LaptopRepairValue = {
    repairCost?: number,
    repairDate?: Date,
    vendor?: string,
};

const StyledField = styled.div`
  width: 70%;

  [data-garden-id="forms.faux_input"] {
      align-items: center;
      height: 40px;
  }
`;

export const RepairInformation = ({ isShow,information,currentTicketId }: RepairInformationProps) => {
    const { fetchRepair, isGetRepairLoading, laptopRepair} = useGetLaptopRepairAsync();
    const { isUpdateRepairLoading, postRepair} = useUpdateLaptopRepairAsync();
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('Please select category as Hardware repair');
    const [showSuccess, setShowSuccess] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingRepairValues, setPendingRepairValues] = useState<LaptopRepairValue | null>(null);
    const [isModalLoading, setIsModalLoading] = useState(false);

    useEffect(() => {
        if (showSuccess) {
            const timer = setTimeout(() => {
                setShowSuccess(false);
            }, 10000);
            return () => clearTimeout(timer);
        }
    }, [showSuccess]);

    useEffect(() => {
        if (showAlert) {
            const timer = setTimeout(() => {
                setShowAlert(false);
            }, 10000);
            return () => clearTimeout(timer);
        }
    }, [showAlert]);

    useEffect(() => {
        if (information && information.serialNumber) {
            // clear repair form when laptop selection change
            reset({
                repairCost: null,
                vendor: null ,
                repairDate: null
            });
            fetchRepair({ticketId: currentTicketId, serialNumber: information.serialNumber});
        }
    }, [information, currentTicketId]);

    const {
        handleSubmit,
        control,
        reset,
        getValues,
        watch,
        formState: { errors, isValid},
    } = useForm<LaptopRepairValue>();

    useEffect(() => {
        if (!isGetRepairLoading) {
            reset({
                repairCost: laptopRepair?.repairCost ? Number(laptopRepair.repairCost) : null,
                vendor: laptopRepair?.vendor || null ,
                repairDate: laptopRepair?.repairDate ? new Date(laptopRepair.repairDate) : null
            });
        }
    }, [isGetRepairLoading]);

    const isFormChanged = useMemo(() => (
        Boolean(!Object.values(watch()).some(value => !!value))
    ), [watch(), information]);

    const purchaseDate = useMemo(() =>
        information?.purchasedDate ? new Date(information.purchasedDate) : undefined
    , [information?.purchasedDate]);

    const handleFormSubmit = (values: LaptopRepairValue) => {
        setPendingRepairValues(values);
        setShowConfirmModal(true);
    };

    const handleModalNo = () => {
        setShowConfirmModal(false);
        setPendingRepairValues(null);
    };

    const handleModalYes = async () => {
        if (!pendingRepairValues) return;
        setIsModalLoading(true);
        // get zendesk Category
        const categoryStr = await getCategory(laptopRepair.customFieldId);
        const category = categoryStr.split('__')[0];
        const subCategory = categoryStr.split('__')[1];
        if (category == 'hardware_repair') {
            const requestBody = {
                identity: {
                    ticketId: currentTicketId,
                    serialNumber: information.serialNumber,
                    ...(laptopRepair?.sysId && {sysId: laptopRepair.sysId})
                },
                repairInfo: {
                    repairCost: pendingRepairValues.repairCost,
                    repairDate: pendingRepairValues.repairDate,
                    vendor: pendingRepairValues.vendor,
                    repairType: subCategory,
                }
            };
            try {
                await postRepair(requestBody);
                setShowSuccess(true);
                fetchRepair({ticketId: currentTicketId, serialNumber: information.serialNumber});
            } catch (e) {
                setAlertMessage('Oops! Something went wrong. Please try again later.');
                setShowAlert(true);
            }
        } else {
            setAlertMessage('Please select category as Hardware repair');
            setShowAlert(true);
        }
        setIsModalLoading(false);
        setShowConfirmModal(false);
        setPendingRepairValues(null);
    };

    return  isShow && !isGetRepairLoading ?
        (
            <>
                {/* Confirmation Modal */}
                <ConfirmSerialNumberModal
                    open={showConfirmModal}
                    onYes={handleModalYes}
                    onNo={handleModalNo}
                    isLoading={isModalLoading}
                />
                <Row justifyContent="center" style={{marginBottom: '12px'}}>
                    <Col sm={8}>
                        <ReplacementForm customFieldId={laptopRepair.customFieldId} purchaseDate={purchaseDate} sysId={information.sysId} planedReplacementDate={information.planedReplacementDate ? new Date(information.planedReplacementDate) : null}/>
                    </Col>
                </Row>
                <form onSubmit={handleSubmit(handleFormSubmit)}>
                    <Row style={{display: 'block', paddingBottom: '20px'}}>
                        <Grid style={{paddingLeft: 0}}>
                            <Row justifyContent="center" style={{marginBottom: '12px'}}>
                                <Col sm={8}>
                                    <Field style={{width: '70%'}}>
                                        <Label>Repair Cost</Label>
                                        <Controller
                                            control={control}
                                            name={'repairCost'}
                                            rules={{
                                                pattern: {
                                                    value: /^\d*\.?\d*$/,
                                                    message: 'Please enter a valid number'
                                                }
                                            }}
                                            render={({ field: { onChange, value } }) => (
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    value={getValues('repairCost')}
                                                    onChange={onChange}
                                                    min="0"
                                                />
                                            )}
                                        />
                                    </Field>
                                </Col>
                            </Row>
                            <Row justifyContent="center" style={{marginBottom: '12px'}}>
                                <Col sm={8}>
                                    <StyledField>
                                        <Label>Repair Date</Label>
                                        <Controller control={control}
                                            name={'repairDate'}
                                            render={
                                                ({ field: { onChange, value } }) =>(
                                                    <Datepicker key="repair-date"
                                                        formatDate={date => dateFormatter.format(date)}
                                                        onChange={onChange}
                                                        value={value}
                                                        minValue={purchaseDate}>
                                                        <MediaInput end={<ClearButton onClick={() => reset({repairDate: null})} />}/>
                                                    </Datepicker>
                                                )
                                            } />
                                    </StyledField>
                                </Col>
                            </Row>
                            <Row justifyContent="center" style={{marginBottom: '12px'}}>
                                <Col sm={8}>
                                    <Field style={{width: '70%'}}>
                                        <Label>Repair Vendor Name</Label>
                                        <Controller control={control}
                                            name={'vendor'}
                                            render={({ field: { onChange, value } }) =>(
                                                <Input value={value} onChange={onChange}/>
                                            )}
                                        />
                                    </Field>
                                </Col>
                            </Row>
                            <Row>
                                {showAlert && (
                                    <Col sm={8}>
                                        <RepairAlert
                                            message={alertMessage}
                                            onClose={() => setShowAlert(false)}
                                            type="error"
                                        />
                                    </Col>
                                )}
                                {showSuccess && (
                                    <Col sm={8}>
                                        <RepairAlert
                                            message="Repair information updated successfully!"
                                            onClose={() => setShowSuccess(false)}
                                            type="success"
                                        />
                                    </Col>
                                )}
                                <Col sm={8}>
                                    <Button size="medium" type={'submit'} disabled={isFormChanged || isUpdateRepairLoading}>Submit</Button>
                                </Col>
                            </Row>
                        </Grid>
                    </Row>
                </form>
            </>
        ) : <></>;
};